import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subject } from 'rxjs';
import { finalize, first, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Account } from '../../models/account';
import { AccountService } from '../../services/account.service';
import { ModalService } from '../../services/modal.service';
import { SiteService } from '../../services/site.service';
import { EVENT_PURPOSES, EventTypeUsage, UsageService } from '../../services/usage.service';

const norm = (v: any) => String(v ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

@Component({
  selector: 'app-account-event-type-list',
  templateUrl: './account-event-type-list.component.html',
  styleUrls: ['./account-event-type-list.component.scss'],
})
export class AccountEventTypeListComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  account$: Observable<Account>;

  /**
   * Purpose (conductor-server #249): what an event of this type uses a venue for. Usage ("places we've used") counts
   * a venue per purpose, so the venue map can rank venues by what they were used for. Unset counts as other.
   */
  readonly purposes = EVENT_PURPOSES;
  /** How often each type is used, and a suggested purpose, keyed by the type's value (from the usage job). */
  usage = new Map<string, EventTypeUsage>();

  constructor(
    private usageService: UsageService,
    private zone: NgZone,
    private siteService: SiteService,
    private modalService: ModalService,
    private accountService: AccountService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.account$ = this.accountService.account$;
  }

  ngOnInit(): void {
    // Best effort: without it the column still works, just without counts and suggestions.
    this.usageService.eventTypes().subscribe({
      next: (rows) => this.zone.run(() => (this.usage = new Map(rows.map((r) => [norm(r.eventType), r])))),
      error: () => undefined,
    });
  }

  usageOf(eventType: any): EventTypeUsage | undefined {
    return this.usage.get(norm(eventType?.value)) ?? this.usage.get(norm(eventType?.name));
  }

  purposeLabel(value: string | undefined): string {
    return this.purposes.find((p) => p.value === value)?.label ?? '';
  }

  onPurposeChange(eventType: any, purpose: string | null): void {
    eventType.purpose = purpose || null;
    this.updateAccountData();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onEditEventType(event: Event, religion: any): void {
    const modal = this.modalService.inputNameValuePair({
      title: 'Event Type',
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-secondary' },
        { label: 'Save', key: 'save', class: '' },
      ],
      data: religion,
    });

    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason && reason.toString().toLowerCase() === 'save') {
          this.updateAccountData();
        }
      },
    });
  }

  onDeleteEventType(event: Event, religion: any): void {
    const modal = this.modalService.generic({
      title: 'Remove Event Type',
      copy: ['Are you sure you want to remove this event type?'],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Confirm', key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });

    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason.toString().toLowerCase() === 'confirm') {
          religion.disabled = true;

          this.updateAccountData();
        }
      },
    });
  }

  onRestoreEventType(event: Event, religion: any): void {
    const modal = this.modalService.generic({
      title: 'Restore Event Type',
      copy: ['Are you sure you want to restore this event type?'],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Confirm', key: 'confirm', class: 'p-button-success', icon: 'pi pi-refresh' },
      ],
    });

    modal.onClose.subscribe({
      next: (reason: string) => {
        if (reason.toString().toLowerCase() === 'confirm') {
          religion.disabled = false;

          this.updateAccountData();
        }
      },
    });
  }

  private updateAccountData(): void {
    this.siteService.addSubscriptionLog(
      this,
      'account-event-type-list.component.ts->updateAccountData->this.accountService.account$'
    );

    this.accountService.account$
      .pipe(
        finalize(() =>
          this.siteService.setSubscriptionLogFinalised(
            'account-event-type-list.component.ts->updateAccountData->this.accountService.account$'
          )
        ),
        takeUntil(this.unsubscribe$),
        first(),
        switchMap((account) => this.accountService.patch(account.data))
      )
      .subscribe({
        next: (res) => {
          // console.log(res.data);
        },
      });
  }
}
